/**
 * Composable pro správu uživatelských rolí a oprávnění
 *
 * The role is shared session state, not per-component refs. It used to be the
 * latter, and every caller then started its own query: a page that asked
 * `isAdmin` in `onMounted` — one tick after the composable was created, long
 * before any answer came back — was told it had no permissions whatever role it
 * actually held. `/admin/users` failed that way on every load, and the
 * dashboard only worked because it polled `userRole` in a 100ms loop first.
 *
 * So: one piece of state via `useState` (also carried across hydration by the
 * payload), and the admin-only methods below await the role through
 * `ensureRoleLoaded` instead of guessing at it.
 */

interface RoleRow {
  role_name: string | null
  permissions: Record<string, boolean> | null
  is_role_active: boolean | null
}

export const useUserRole = () => {
  const user = useSupabaseUser()
  const supabase = useSupabaseClient()

  // Reactive state pro uživatelskou roli
  const userRole = useState<string | null>('user-role', () => null)
  const userPermissions = useState<Record<string, boolean>>('user-role-permissions', () => ({}))
  const isLoading = useState<boolean>('user-role-loading', () => false)
  const error = useState<string | null>('user-role-error', () => null)

  // Computed properties pro rychlé kontroly rolí
  const isAdmin = computed(() => userRole.value === 'Admin')
  const isUser = computed(() => userRole.value === 'User')
  const isAuthenticated = computed(() => !!user.value)

  // Computed properties pro oprávnění
  const canAccessAdmin = computed(() => userPermissions.value.can_access_admin === true)
  const canManageUsers = computed(() => userPermissions.value.can_manage_users === true)
  const canViewAnalytics = computed(() => userPermissions.value.can_view_analytics === true)
  const canExportAnalytics = computed(() => userPermissions.value.can_export_analytics === true)
  const canManageRoles = computed(() => userPermissions.value.can_manage_roles === true)

  /** The active role row for one user, or null when they have none yet. */
  const queryRole = async (userId: string): Promise<RoleRow | null> => {
    const { data, error: fetchError } = await supabase
      .from('v_user_roles_secure')
      .select('role_name, permissions, is_role_active')
      .eq('user_id', userId)
      .eq('is_role_active', true)
      .single()

    // PGRST116 is "no row", which is a state we recover from, not an error.
    if (fetchError && fetchError.code !== 'PGRST116') throw fetchError
    return (data as RoleRow | null) ?? null
  }

  /**
   * Writes a role assignment; the permission check belongs to the caller.
   * These are the steps of assign_user_role() in database-user-roles-schema.sql,
   * which was never created in the database. RLS lets only an admin write
   * user_role_assignments (20261015_lock_down_permissions.sql).
   */
  const writeRoleAssignment = async (userId: string, roleName: string): Promise<void> => {
    const { data: role, error: roleError } = await supabase
      .from('user_roles')
      .select('id')
      .eq('role_name', roleName)
      .single()
    if (roleError) throw roleError
    const roleId = (role as { id: number } | null)?.id
    if (!roleId) throw new Error(`Role ${roleName} not found`)

    const { error: offError } = await supabase
      .from('user_role_assignments')
      .update({ is_active: false } as never)
      .eq('user_id', userId)
      .eq('is_active', true)
    if (offError) throw offError

    const { error: insertError } = await supabase
      .from('user_role_assignments')
      .insert({ user_id: userId, role_id: roleId, assigned_by: user.value?.id ?? null, expires_at: null } as never)
    if (insertError) throw insertError
  }

  /** The User role, for an account nobody has assigned one. */
  const defaultRole = async (): Promise<RoleRow | null> => {
    const { data, error: fetchError } = await supabase
      .from('user_roles')
      .select('role_name, permissions')
      .eq('role_name', 'User')
      .single()
    if (fetchError && fetchError.code !== 'PGRST116') throw fetchError
    const role = data as { role_name: string, permissions: Record<string, boolean> | null } | null
    return role ? { ...role, is_role_active: true } : null
  }

  /**
   * Načtení role aktuálního uživatele
   *
   * Always queries. `ensureRoleLoaded` is the entry point for "I need the role
   * before I can decide something", and skips the query once it is in hand.
   */
  const fetchUserRole = async (): Promise<void> => {
    if (!user.value) {
      userRole.value = null
      userPermissions.value = {}
      return
    }

    isLoading.value = true
    error.value = null

    try {
      let row = await queryRole(user.value.id)

      // Nobody has assigned this account a role yet, as with almost every
      // account: it is a User. Only an admin may write user_role_assignments,
      // so the default is read, not stored.
      if (!row) row = await defaultRole()

      if (row) {
        userRole.value = row.role_name
        userPermissions.value = row.permissions || {}
      }
    } catch (err: any) {
      console.error('Error fetching user role:', err)
      error.value = err.message || 'Failed to fetch user role'

      // Fallback - nastavíme základní User roli
      userRole.value = 'User'
      userPermissions.value = {
        can_view_profile: true,
        can_edit_profile: true,
        can_create_cv: true,
        can_export_cv: true,
        can_access_admin: false
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Resolves once this session's role is known.
   *
   * Anything that reads `isAdmin` to decide whether it may proceed has to await
   * this first, or it is reading the state from before the role arrived.
   */
  const ensureRoleLoaded = async (): Promise<void> => {
    if (userRole.value) return
    await fetchUserRole()
  }

  /**
   * Kontrola konkrétního oprávnění
   */
  const hasPermission = (permission: string): boolean => {
    return userPermissions.value[permission] === true
  }

  /**
   * Kontrola více oprávnění najednou (všechna musí být splněna)
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every(permission => hasPermission(permission))
  }

  /**
   * Kontrola více oprávnění najednou (alespoň jedno musí být splněno)
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(permission => hasPermission(permission))
  }

  /**
   * Přiřazení role uživateli (pouze pro adminy)
   */
  const assignRole = async (userId: string, roleName: string): Promise<boolean> => {
    const isSelf = user.value?.id === userId
    // Assigning to yourself is allowed without a role; anything else is an
    // admin action, so the role has to be known before it can be refused.
    if (!isSelf) await ensureRoleLoaded()

    if (!isAdmin.value && !isSelf) {
      throw new Error('Insufficient permissions to assign roles')
    }

    try {
      await writeRoleAssignment(userId, roleName)

      // Pokud měníme roli sobě, aktualizujeme lokální state
      if (isSelf) {
        await fetchUserRole()
      }

      return true
    } catch (err: any) {
      console.error('Error assigning role:', err)
      throw new Error(err.message || 'Failed to assign role')
    }
  }

  /**
   * Načte všechny řádky dotazu po stránkách (obchází limit 1000 řádků Supabase)
   */
  const fetchAllRows = async <T>(
    query: (from: number, to: number) => PromiseLike<{ data: T[] | null, error: any }>
  ): Promise<T[]> => {
    const pageSize = 1000
    const rows: T[] = []
    for (let from = 0; ; from += pageSize) {
      const { data, error } = await query(from, from + pageSize - 1)
      if (error) throw error
      rows.push(...(data || []))
      if (!data || data.length < pageSize) break
    }
    return rows
  }

  /**
   * Získání seznamu všech uživatelů s rolemi (pouze pro adminy)
   */
  const getAllUsersWithRoles = async () => {
    await ensureRoleLoaded()

    if (!isAdmin.value) {
      throw new Error('Insufficient permissions to view all users')
    }

    try {
      // Načteme základní info o uživatelích
      // Supabase vrací max 1000 řádků na dotaz, proto načítáme po stránkách
      const usersData = await fetchAllRows<any>((from, to) =>
        supabase
          .from('v_user_roles_secure')
          .select('user_id, email, role_name, permissions, assigned_at, is_role_active')
          .order('email')
          .order('user_id')
          .range(from, to)
      )

      // Načteme tracking data z profiles
      const profilesData = await fetchAllRows<any>((from, to) =>
        supabase
          .from('profiles')
          .select('id, premium_checkout_shown_at, presell_offer_shown_at')
          .order('id')
          .range(from, to)
      )

      // Spojíme data
      const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || [])

      const usersWithTracking = (usersData || []).map(user => {
        const profile = profilesMap.get(user.user_id)
        return {
          ...user,
          premium_checkout_shown_at: profile?.premium_checkout_shown_at || null,
          presell_offer_shown_at: profile?.presell_offer_shown_at || null
        }
      })

      return usersWithTracking
    } catch (err: any) {
      console.error('Error fetching users with roles:', err)
      throw new Error(err.message || 'Failed to fetch users')
    }
  }

  /**
   * Počty uživatelů a poslední registrace pro dashboard (pouze pro adminy)
   * Počítá se přes count na serveru, bez stahování všech řádků
   */
  const getUserStats = async (recentLimit = 10) => {
    await ensureRoleLoaded()

    if (!isAdmin.value) {
      throw new Error('Insufficient permissions to view all users')
    }

    const [total, admins, recent] = await Promise.all([
      supabase
        .from('v_user_roles_secure')
        .select('user_id', { count: 'exact', head: true }),
      supabase
        .from('v_user_roles_secure')
        .select('user_id', { count: 'exact', head: true })
        .eq('role_name', 'Admin'),
      supabase
        .from('v_user_roles_secure')
        .select('user_id, email, role_name, assigned_at')
        .order('assigned_at', { ascending: false, nullsFirst: false })
        .limit(recentLimit)
    ])

    const error = total.error || admins.error || recent.error
    if (error) {
      console.error('Error fetching user stats:', error)
      throw new Error(error.message || 'Failed to fetch user stats')
    }

    return {
      usersCount: total.count || 0,
      adminsCount: admins.count || 0,
      recentUsers: recent.data || []
    }
  }

  /**
   * Získání seznamu dostupných rolí (pouze pro adminy)
   */
  const getAvailableRoles = async () => {
    await ensureRoleLoaded()

    if (!isAdmin.value) {
      throw new Error('Insufficient permissions to view roles')
    }

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('id, role_name, role_description, permissions')
        .order('role_name')

      if (error) throw error
      return data || []
    } catch (err: any) {
      console.error('Error fetching roles:', err)
      throw new Error(err.message || 'Failed to fetch roles')
    }
  }

  /**
   * Vytvoření prvního admin uživatele
   */
  const createFirstAdmin = async (email: string): Promise<string> => {
    try {
      const { data, error } = await supabase.rpc('create_first_admin', {
        p_email: email
      } as any)

      if (error) throw error
      return data as string
    } catch (err: any) {
      console.error('Error creating first admin:', err)
      throw new Error(err.message || 'Failed to create first admin')
    }
  }

  /**
   * Kontrola, zda existuje nějaký admin
   */
  const checkAdminExists = async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('v_user_roles_secure')
        .select('user_id')
        .eq('role_name', 'Admin')
        .eq('is_role_active', true)
        .limit(1)

      if (error) throw error
      return (data || []).length > 0
    } catch (err: any) {
      console.error('Error checking admin exists:', err)
      return false
    }
  }

  /**
   * Watch změny uživatele a automaticky načítej roli
   */
  watch(user, async (newUser, oldUser) => {
    if (!newUser) {
      userRole.value = null
      userPermissions.value = {}
      return
    }

    // A different account signed in: its predecessor's role must not survive.
    if (oldUser && oldUser.id !== newUser.id) {
      userRole.value = null
      userPermissions.value = {}
    }

    await ensureRoleLoaded()
  }, { immediate: true })

  return {
    // State
    userRole: readonly(userRole),
    userPermissions: readonly(userPermissions),
    isLoading: readonly(isLoading),
    error: readonly(error),

    // Computed
    isAdmin,
    isUser,
    isAuthenticated,
    canAccessAdmin,
    canManageUsers,
    canViewAnalytics,
    canExportAnalytics,
    canManageRoles,

    // Methods
    fetchUserRole,
    ensureRoleLoaded,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    assignRole,
    getAllUsersWithRoles,
    getUserStats,
    getAvailableRoles,
    createFirstAdmin,
    checkAdminExists
  }
}
